import { Form, Formik } from 'formik';
import React, { useRef } from 'react';
import { Flex } from 'antd';
import Authwrapper from './authwrapper';
import * as yup from 'yup';
import { Button, Input } from 'common';
import { SecureIcon } from 'common/all-icons/custom-icons';
import { currentBaseRoute, getErrorString } from 'utils';
import toast from 'react-hot-toast';
import { onboardingApi } from 'api/auth-user-api';
import { useDispatch } from 'react-redux';
import { setPatientUser } from 'reducers/patient-user-slice';
import { removeSingleCookie, setCookie } from 'utils/cookies-util';
import { Link, useNavigate } from 'react-router-dom';
import { USER_SIGNIN_PATH } from 'constants/user-paths';
import { regex } from 'constants/generic-regex';

export default function AccountActivation({ handleLogin = () => {} }) {
  const formikRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');

  const getInitialValues = () => ({
    password: '',
    confirm_password: '',
  });

  const getValidationSchema = () =>
    yup.object().shape({
      password: yup
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(64, 'Password must not exceed 64 characters')
        .matches(regex.password, 'Password must contain only letters, numbers, and symbols. Spaces are not allowed.')
        .required('Password is required'),
      confirm_password: yup
        .string()
        .oneOf([yup.ref('password')], 'Passwords do not match')
        .required('Confirm password is required'),
    });

  const onSubmit = async (values, { setSubmitting }) => {
    if (!token) return;
    setSubmitting(true);
    try {
      const payload = {
        password: values.password,
      };

      const res = await onboardingApi({ data: payload, token });
      if (res && res?.status) {
        dispatch(setPatientUser(res?.data?.data ?? {}));
        setCookie('access-token-user', res.headers['authorization']);
        removeSingleCookie('resend-time');
        removeSingleCookie('resend-link-time');
        handleLogin();
        navigate(currentBaseRoute());
        return;
      } else {
        toast.error(res?.message ?? 'Onboarding Failed');
        return;
      }
    } catch (error) {
      toast.error(getErrorString(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Authwrapper center={true}>
      <Flex vertical gap={10} className={'authWrap'}>
        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          validationSchema={getValidationSchema()}
          onSubmit={onSubmit}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit, isSubmitting, status }) => (
            <Form onSubmit={handleSubmit} className={'authWrap'}>
              <Flex vertical gap={24}>
                <Flex vertical gap={40}>
                  <Flex vertical gap={16}>
                    <div className="lock-wrap">
                      <SecureIcon className="primary-text" size={24} />
                    </div>
                    <Flex vertical gap={8}>
                      <h2 className="heading-text">Set your password to access the Patient Portal</h2>
                      <h5 className="subheading-text">
                        Create a secure password to access your Patient Portal and view your order details.
                      </h5>
                    </Flex>
                  </Flex>

                  <Flex vertical gap={20}>
                    <Input
                      isPassword
                      className={'h5-regular input-box user'}
                      placeholder="Enter Password"
                      name="password"
                      type="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      errorMsg={touched.password && errors.password}
                    />

                    <Input
                      isPassword
                      className={'h5-regular input-box user'}
                      placeholder="Confirm Password"
                      name="confirm_password"
                      type="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.confirm_password}
                      errorMsg={touched.confirm_password && errors.confirm_password}
                    />
                  </Flex>
                </Flex>

                <Button
                  className={'body-14-semibold primary-button'}
                  loading={isSubmitting}
                  text={isSubmitting ? 'Activating' : 'Activate Account'}
                  type="primary"
                  size="large"
                  htmlType="submit"
                />

                {status && status !== 'SUCCESS' && (
                  <div className="body-12-semibold error-text text-center">{status}</div>
                )}

                <Flex gap={8} justify="center" align="center">
                  <SecureIcon style={{ color: 'var(--user-main-green)' }} />
                  <div className="body-12-semibold">Your information is secure and encrypted.</div>
                </Flex>
              </Flex>

              <Flex justify="center" align="center" gap={5} className="mt-2">
                <div className="text-navigate">Already have an account?</div>
                <Link className="button-navigate" to={USER_SIGNIN_PATH}>
                  Login
                </Link>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
    </Authwrapper>
  );
}
